import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

// Asset Registry: where a multi-reading device's readings come from — an IAQ sensor or an energy
// meter. Such a device is one Modbus slave behind a gateway that reports several registers, so
// instead of the single Live Data Source link this picks the slave, then a register (and scale) per
// reading. The register list is what the slave is reporting right now, with values, so the person
// wiring it up can check the numbers. Saved into meta.{reading}_register / meta.{reading}_scale —
// read by backend/iaq.py and backend/energy.py. The schema field (registrySchemas.js, kind
// "slave_readings") lists the readings.

const SCALES = ["1", "0.1", "0.01", "0.001"];
const NONE = "_none";

const Label = ({ children }) => <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{children}</span>;

export default function SlaveReadingsFields({ form, setForm, gateways, siteId, readings, hint }) {
  const meta = form.meta || {};
  const [slaves, setSlaves] = useState([]);
  const [rows, setRows] = useState([]);
  const setMeta = (patch) => setForm((f) => ({ ...f, meta: { ...(f.meta || {}), ...patch } }));

  useEffect(() => {
    if (!form.gateway_id) { setSlaves([]); return; }
    api.get(`/hardware/gateways/${form.gateway_id}/slaves`).then((r) => setSlaves(r.data || [])).catch(() => setSlaves([]));
  }, [form.gateway_id]);

  useEffect(() => {
    if (!siteId || !form.gateway_id) { setRows([]); return; }
    api.get(`/v1/sites/${siteId}/telemetry/latest`, { params: { limit: 2000 } })
      .then((r) => setRows((r.data || []).filter((x) => x.gateway_id === form.gateway_id)))
      .catch(() => setRows([]));
  }, [siteId, form.gateway_id]);

  // Registered slaves (with their labels) plus any slave reporting without being registered
  const slaveOptions = useMemo(() => {
    const m = new Map();
    slaves.forEach((s) => m.set(String(s.slave_id), s.label || ""));
    rows.forEach((x) => { if (x.slave_id != null && !m.has(String(x.slave_id))) m.set(String(x.slave_id), ""); });
    if (meta.slave_id && !m.has(String(meta.slave_id))) m.set(String(meta.slave_id), "");
    return [...m.entries()].sort((a, b) => Number(a[0]) - Number(b[0]) || a[0].localeCompare(b[0]));
  }, [slaves, rows, meta.slave_id]);

  const slaveRows = rows.filter((x) => String(x.slave_id) === String(meta.slave_id ?? ""));
  const reporting = new Set(rows.map((x) => String(x.slave_id)));
  const lastAt = slaveRows[0]?.ingested_at;

  return (
    <div className="col-span-2 border-t border-gray-200 pt-3 mt-1 space-y-3" data-testid="slave-readings-fields">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Where the readings come from *</div>
        <div className="text-[10px] text-gray-500 mt-0.5">The gateway and Modbus slave the device is wired to, then which register carries each reading.</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <Label>Gateway</Label>
          <Select value={form.gateway_id || NONE} onValueChange={(v) => setForm((f) => ({ ...f, gateway_id: v === NONE ? "" : v }))}>
            <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="readings-gateway"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent className="bg-white border-gray-200 text-gray-900">
              <SelectItem value={NONE}>None</SelectItem>
              {gateways.map((g) => <SelectItem key={g.gateway_id} value={g.gateway_id}>{g.gateway_name || g.gateway_id}</SelectItem>)}
            </SelectContent>
          </Select>
        </label>
        <label className="flex flex-col gap-1">
          <Label>Slave ID</Label>
          <Select value={meta.slave_id ? String(meta.slave_id) : NONE} onValueChange={(v) => setMeta({ slave_id: v === NONE ? "" : v })} disabled={!form.gateway_id}>
            <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="readings-slave"><SelectValue placeholder={form.gateway_id ? "Select…" : "Select a gateway first"} /></SelectTrigger>
            <SelectContent className="bg-white border-gray-200 text-gray-900">
              <SelectItem value={NONE}>None</SelectItem>
              {slaveOptions.map(([sid, label]) => (
                <SelectItem key={sid} value={sid}>
                  {sid}{label ? ` — ${label}` : ""}{reporting.has(sid) ? " · reporting" : " · no data"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      </div>

      {meta.slave_id && (
        <div className="space-y-2">
          <div className="grid grid-cols-[130px_minmax(0,1fr)_90px_90px] gap-2 items-center">
            <Label>Reading</Label><Label>Register on slave {meta.slave_id}</Label><Label>Scale</Label><Label>Now</Label>
            {readings.map((m) => {
              const reg = meta[`${m.key}_register`] || "";
              const scale = String(meta[`${m.key}_scale`] || "1");
              const row = slaveRows.find((x) => x.parameter === reg);
              const raw = row ? Number(row.value) : null;
              const now = raw != null && Number.isFinite(raw) ? +(raw * Number(scale)).toFixed(3) : null;
              return (
                <React.Fragment key={m.key}>
                  <span className="text-sm">{m.label}{m.required && " *"}</span>
                  {slaveRows.length ? (
                    <Select value={reg || NONE} onValueChange={(v) => setMeta({ [`${m.key}_register`]: v === NONE ? "" : v })}>
                      <SelectTrigger className="bg-gray-50 border-gray-200 h-9" data-testid={`reg-${m.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-gray-200 text-gray-900">
                        <SelectItem value={NONE}>— not measured —</SelectItem>
                        {reg && !row && <SelectItem value={reg}>{reg} (no data now)</SelectItem>}
                        {slaveRows.map((x) => <SelectItem key={x.parameter} value={x.parameter}>{x.parameter} — {x.value}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={reg} onChange={(e) => setMeta({ [`${m.key}_register`]: e.target.value.trim() })} placeholder="register name, or blank" className="bg-gray-50 border-gray-200 h-9" data-testid={`reg-${m.key}`} />
                  )}
                  <Select value={scale} onValueChange={(v) => setMeta({ [`${m.key}_scale`]: v })} disabled={!reg}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 h-9" data-testid={`scale-${m.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-900">
                      {SCALES.map((s) => <SelectItem key={s} value={s}>×{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <span className="text-xs tabular-nums text-gray-700">{now != null ? `${now} ${m.unit}` : "—"}</span>
                </React.Fragment>
              );
            })}
          </div>
          <p className="text-[10px] text-gray-500 leading-snug">
            {slaveRows.length
              ? <>Values as slave {meta.slave_id} reported them {lastAt ? `at ${new Date(lastAt).toLocaleTimeString("en-IN")}` : "last"}. {hint}</>
              : <>Slave {meta.slave_id} isn't reporting right now, so type the register names as configured on the gateway. {hint}</>}
          </p>
        </div>
      )}
    </div>
  );
}
